/**
 * BreadcrumbContext — lets a detail page tell the TopBar what label to
 * show as the trailing crumb (e.g. the server name instead of the id).
 *
 * Usage in a detail route:
 *
 *   const { setDetailLabel } = useBreadcrumb();
 *   useEffect(() => { setDetailLabel(server.name); return () => setDetailLabel(null); }, [server.name]);
 *
 * The TopBar reads `detailLabel` and passes it to `breadcrumbForPath`.
 */
import { createContext, useContext, useState, type JSX, type ReactNode } from 'react';

interface BreadcrumbContextValue {
  readonly detailLabel: string | null;
  readonly setDetailLabel: (label: string | null) => void;
}

const Ctx = createContext<BreadcrumbContextValue | null>(null);

export function BreadcrumbProvider({ children }: { children: ReactNode }): JSX.Element {
  const [detailLabel, setDetailLabel] = useState<string | null>(null);
  return <Ctx.Provider value={{ detailLabel, setDetailLabel }}>{children}</Ctx.Provider>;
}

export function useBreadcrumb(): BreadcrumbContextValue {
  const v = useContext(Ctx);
  if (!v) {
    // Outside provider: return a no-op so detail pages don't blow up if mounted bare.
    return { detailLabel: null, setDetailLabel: () => {} };
  }
  return v;
}
